import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import MediaQuery from 'react-responsive';
import styles from './Settings.module.sass';
import ClickableDiv from '../../../../ui_kit/clickable_div/ClickableDiv';
import MinusSign from '../../minus_sign/MinusSign';
import PlusSign from '../../plus_sign/PlusSign';
import COMPLEXITY, { SIMPLE_PRODUCTS_COUNT } from '../../../constants/complexity';
import DAY_OR_NIGHT from '../../../constants/day_or_night';
import BREAKPOINTS from '../../../constants/breakpoints';
import defaultSelectedCategories from '../default_categories';

function Settings(props) {
  const {
    complexity, setComplexity, dayOrNight, setDayOrNight,
    availableCategories, setSelectedCategories, selectedCategories,
    productsCount,
  } = props;

  const [showMoreContent, setShowMoreContent] = useState(true);

  const complexityContent = (
    <div className={styles.section}>
      <div className={styles.title}>
        Complexity
      </div>
      <div className={`${styles.blocks} ${styles['desktop-column']}`}>
        <ClickableDiv
          className={`${styles.block} ${complexity === COMPLEXITY.SIMPLE ? styles.active : ''}`}
          onClick={() => {
            if (complexity === COMPLEXITY.SIMPLE) { return; }

            setComplexity(COMPLEXITY.SIMPLE);
            setSelectedCategories(defaultSelectedCategories(COMPLEXITY.SIMPLE, dayOrNight));
          }}
        >
          {COMPLEXITY.SIMPLE}
          <div className={styles.description}>
            {`1-${SIMPLE_PRODUCTS_COUNT} products`}
          </div>
        </ClickableDiv>
        <ClickableDiv
          className={`${styles.block} ${complexity === COMPLEXITY.COMPLEX ? styles.active : ''}`}
          onClick={() => {
            if (complexity === COMPLEXITY.COMPLEX) { return; }

            setComplexity(COMPLEXITY.COMPLEX);
            setSelectedCategories(defaultSelectedCategories(COMPLEXITY.COMPLEX, dayOrNight));
          }}
        >
          {COMPLEXITY.COMPLEX}
          <div className={styles.description}>
            {`${SIMPLE_PRODUCTS_COUNT}+ products`}
          </div>
        </ClickableDiv>
      </div>
    </div>
  );

  const categoriesContent = (
    <div className={styles.section}>
      <div className={styles.title}>
        Products
      </div>
      <div className={`${styles.blocks} ${styles['desktop-column']}`}>
        { availableCategories.map((category) => (
          <ClickableDiv
            key={category}
            className={`${styles.block} ${selectedCategories.includes(category) ? styles.active : ''}`}
            onClick={() => {
              setSelectedCategories(
                selectedCategories.includes(category)
                  ? selectedCategories.filter((selectedCategory) => selectedCategory !== category)
                  : availableCategories.filter((availableCategory) => selectedCategories.includes(
                    availableCategory,
                  ) || availableCategory === category),
              );
            }}
          >
            {category}
          </ClickableDiv>
        ))}
      </div>
    </div>
  );

  const dayOrNightContent = (
    <div className={styles.section}>
      <div className={styles.title}>
        Day or Night?
      </div>
      <div className={styles.blocks}>
        <ClickableDiv
          className={`${styles.block} ${dayOrNight === DAY_OR_NIGHT.DAY ? styles.active : ''}`}
          onClick={() => {
            if (dayOrNight === DAY_OR_NIGHT.DAY) { return; }

            setDayOrNight(DAY_OR_NIGHT.DAY);
            setSelectedCategories(defaultSelectedCategories(complexity, DAY_OR_NIGHT.DAY));
          }}
        >
          {DAY_OR_NIGHT.DAY}
        </ClickableDiv>
        <ClickableDiv
          className={`${styles.block} ${dayOrNight === DAY_OR_NIGHT.NIGHT ? styles.active : ''}`}
          onClick={() => {
            if (dayOrNight === DAY_OR_NIGHT.NIGHT) { return; }

            setDayOrNight(DAY_OR_NIGHT.NIGHT);
            setSelectedCategories(defaultSelectedCategories(complexity, DAY_OR_NIGHT.NIGHT));
          }}
        >
          {DAY_OR_NIGHT.NIGHT}
        </ClickableDiv>
      </div>
    </div>
  );

  return (
    <div className={styles.settings}>
      <MediaQuery query={`(max-width: ${BREAKPOINTS.DESKTOP_WIDTH_BREAKPOINT - 1}px)`}>
        <div className={styles.header}>
          {`${complexity} ${dayOrNight}, ${productsCount} products`}
        </div>
        <div className={styles['accordion-content']}>
          {complexityContent}
          <ClickableDiv className={styles['show-more']} onClick={() => { setShowMoreContent(!showMoreContent); }}>
            {showMoreContent ? <MinusSign /> : <PlusSign />}
            {showMoreContent ? 'Less' : 'More'}
          </ClickableDiv>
          <AnimateHeight
            duration={400}
            height={showMoreContent ? 'auto' : 0}
          >
            <div style={{ marginTop: '24px', marginBottom: '26px' }}>
              {dayOrNightContent}
              {categoriesContent}
            </div>
          </AnimateHeight>
        </div>
      </MediaQuery>
      <MediaQuery query={`(min-width: ${BREAKPOINTS.DESKTOP_WIDTH_BREAKPOINT}px)`}>
        <div className={styles['accordion-content']}>
          {complexityContent}
          {dayOrNightContent}
          {categoriesContent}
        </div>
      </MediaQuery>
    </div>
  );
}

Settings.propTypes = {
  setComplexity: PropTypes.func.isRequired,
  setDayOrNight: PropTypes.func.isRequired,
  complexity: PropTypes.oneOf([COMPLEXITY.SIMPLE, COMPLEXITY.COMPLEX]).isRequired,
  dayOrNight: PropTypes.oneOf(Object.values(DAY_OR_NIGHT)).isRequired,
  availableCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedCategories: PropTypes.func.isRequired,
  productsCount: PropTypes.number,
};

export default Settings;
